import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ForumPost, ApiForumPost } from '../../models/ForumPost';
import { useApi } from '../../hooks/useApi';
import { AGE_RANGE_TEXTS, BODY_FIGURE_TEXTS, HEIGHT_RANGE_TEXTS } from '../../consts';
import { toast } from 'react-toastify';
import './MyPostCard.scss';

type Props = {
  post: ForumPost;
  onUpdated: (id: number, updated: ApiForumPost) => void;
};

const STATUS_PILL: Record<string, { label: string; cls: string }> = {
  OPEN:  { label: '掲載中', cls: 'open' },
  DRAFT: { label: '下書き', cls: 'draft' },
};

const MyPostCard = ({ post, onUpdated }: Props) => {
  const navigate = useNavigate();
  const { post: apiPost, loading } = useApi();

  const isExpired = post.isOpen && !!post.expiresAt && new Date(post.expiresAt) < new Date();
  const pill = isExpired
    ? { label: '掲載終了', cls: 'closed' }
    : STATUS_PILL[post.status] ?? { label: post.status, cls: 'draft' };
  const isUrgent = post.isOpen && !isExpired && post.daysLeft > 0 && post.daysLeft <= 20;
  const deadlineRel = post.isOpen
    ? `あと${ post.daysLeft }日`
    : '未投稿';

  const handleClose = () => {
    if (!window.confirm('この募集を締め切りますか？')) return;
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const yesterday = `${ d.getFullYear() }-${ String(d.getMonth() + 1).padStart(2, '0') }-${ String(d.getDate()).padStart(2, '0') }`;
    apiPost({
      endpoint: `api/v1/forum_posts/${ post.id }`,
      method: 'PATCH',
      body: { expires_at: yesterday },
      onSuccess: (res) => {
        toast.success('募集を締め切りました');
        onUpdated(post.id, res.data as ApiForumPost);
      },
      onError: () => { toast.error('操作に失敗しました'); },
    });
  };

  const titleText = post.description
    ? post.description
    : post.area
    ? `${ post.areaText }のドナーを募集しています`
    : '（内容を入力してください）';

  return (
    <li className={ `mp-post status-${ pill.cls }` }>
      <div className="top-row">
        <div className="meta-stack">
          <div className="author-line">
            <span className={ `status-pill ${ pill.cls }` }>{ pill.label }</span>
          </div>
          <div className="sub-meta">
            <span>投稿 <strong>{ post.postedAtText || '—' }</strong></span>
            { post.status !== 'DRAFT' && post.expiresAt && (
              <>
                <span className="dot" />
                <span>
                  締切 <strong>{ post.expiresAtText }</strong>
                  { !isExpired && (
                    <em className={ `countdown${ isUrgent ? ' urgent' : '' }` }>{ deadlineRel }</em>
                  ) }
                </span>
              </>
            ) }
          </div>
          <h3 className="mp-title">{ titleText }</h3>
        </div>

        <div className="right-meta">
          { post.applicationsCount > 0 ? (
            <Link
              to={ `/mypage/forum/${ post.id }/applications` }
              className="apps apps-link"
            >
              <span className="num">{ post.applicationsCount }<span className="cap">/20</span></span>
              <span className="lbl">人 応募</span>
            </Link>
          ) : (
            <div className="apps apps-link is-empty">
              <span className="num">{ post.applicationsCount }<span className="cap">/20</span></span>
              <span className="lbl">人 応募</span>
            </div>
          ) }
        </div>
      </div>

      <div className="conditions">
        <h4>募集内容</h4>
        <dl>
          <dt>エリア</dt>
          <dd className="chips">
            <span className="chip area">{ post.areaText || '未設定' }</span>
          </dd>

          { post.bloodTypeChips.length > 0 && (
            <>
              <dt>血液型</dt>
              <dd className="chips">
                { post.bloodTypeChips.map(v => (
                  <span key={ v } className="chip">{ v }</span>
                )) }
              </dd>
            </>
          ) }

          { post.ageRange?.length && (
            <>
              <dt>年齢</dt>
              <dd className="chips">
                { post.ageRange.map(k => (
                  <span key={ k } className="chip">{ AGE_RANGE_TEXTS[k] || k }</span>
                )) }
              </dd>
            </>
          ) }

          { post.bodyFigure?.length && (
            <>
              <dt>体型</dt>
              <dd className="chips">
                { post.bodyFigure.map(k => (
                  <span key={ k } className="chip">{ BODY_FIGURE_TEXTS[k] || k }</span>
                )) }
              </dd>
            </>
          ) }

          { post.heightRange?.length && (
            <>
              <dt>身長</dt>
              <dd className="chips">
                { post.heightRange.map(k => (
                  <span key={ k } className="chip">{ HEIGHT_RANGE_TEXTS[k] || k }</span>
                )) }
              </dd>
            </>
          ) }
        </dl>
      </div>

      <div className="mp-actions">
        { post.status === 'DRAFT' ? (
          <>
            <button
              className="mp-btn pink"
              onClick={ () => navigate(`/mypage/forum/${ post.id }/edit`) }
              disabled={ loading }
            >編集して投稿</button>
          </>
        ) : (
          <>
            { !isExpired && (
              <button
                className="mp-btn w-gray"
                onClick={ handleClose }
                disabled={ loading }
              >締め切る</button>
            ) }
            <Link
              to={ `/mypage/forum/${ post.id }/applications` }
              className="mp-btn pink"
            >
              応募者を見る（{ post.applicationsCount }）
            </Link>
          </>
        ) }
      </div>
    </li>
  );
};

export default MyPostCard;
